import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import * as authApi from '@/api/auth';
import type { UserInfo } from '@/api/auth';

/**
 * Auth state. The actual credential is the PHPSESSID cookie managed by the
 * PHP backend — we never hold a token in JS. This store just caches the
 * current user info so views don't have to refetch it.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserInfo | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => user.value !== null);

  async function acceptExistingLoginSession(): Promise<boolean> {
    const info = await authApi.getUserInfo();
    if (info && info.userId) {
      user.value = info;
      return true;
    }
    return false;
  }

  async function login(userName: string, password: string): Promise<boolean> {
    loading.value = true;
    error.value = null;
    try {
      // See admin/src/stores/auth.ts for the full envelope-shape rationale.
      let loginResult: { authenticated?: boolean } | null = null;
      try {
        const r = await authApi.login(userName, password);
        loginResult = r && typeof r === 'object' ? r : null;
      } catch (e: any) {
        if (await acceptExistingLoginSession()) return true;
        error.value = e?.message || 'Login failed';
        return false;
      }

      if (loginResult === null) {
        if (await acceptExistingLoginSession()) return true;
        error.value = 'Invalid username or password';
        return false;
      }

      if (!loginResult.authenticated) {
        error.value = 'Invalid username or password';
        return false;
      }

      const info = await authApi.getUserInfo();
      if (!info?.userId) {
        error.value =
          'Signed in, but the session did not persist. ' +
          'Check that cookies are enabled and try again.';
        return false;
      }
      user.value = info;
      return true;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } catch {
      // Server-side logout errors shouldn't prevent client-side cleanup
    }
    user.value = null;
  }

  async function checkSession(): Promise<boolean> {
    try {
      const info = await authApi.getUserInfo();
      if (info && info.userId) {
        user.value = info;
        return true;
      }
      user.value = null;
      return false;
    } catch {
      user.value = null;
      return false;
    }
  }

  function reset(): void {
    user.value = null;
    error.value = null;
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    logout,
    checkSession,
    reset,
  };
});
